import { FunnelStatusName } from 'src/modules/shared/components/atoms/funnel-status-name';
import { Typography } from '@material-tailwind/react';
import { StyledIcon } from 'src/modules/shared/components/atoms/icons/styled-icon';
import {
  PiChatCircle,
  PiCaretRightBold,
  PiArchiveLight,
  PiArrowCounterClockwiseLight,
} from 'react-icons/pi';
import {
  ChannelType,
  FunnelInteractionDirection,
  FunnelInteractionType,
} from 'src/api/generated';
import type {
  FunnelConversationDocument,
  FunnelStatus,
} from 'src/api/generated';
import { formatDate } from 'src/modules/shared/utils/formatting/date.util';
import { formatFullName } from 'src/modules/shared/utils/formatting/name.util';
import type { TableColumnProps } from 'src/modules/shared/components/molecules/table/table';
import { TagDropdown } from 'src/modules/shared/components/molecules/dropdowns/tag-dropdown';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import { DirectionIndicator } from 'src/modules/shared/components/atoms/indicators/direction-indicator';
import { CallStatusIndicator } from 'src/modules/shared/components/atoms/indicators/call-status-indicator';
import {
  tableCellText,
  tableVariantClasses,
} from 'src/modules/shared/components/molecules/table/styles';
import { isValidPhoneNumber } from 'src/utils/validation/phone';
import { EditableAddressField } from 'src/modules/communications/components/molecules/contacts/editable-address-field';
import {
  getActionTooltip,
  isActionDisabled,
} from 'src/modules/communications/utils/quick-phone.util';
import { AnimatedDot } from 'src/modules/shared/components/atoms/indicators/animated-dot';
import Tooltip from 'src/modules/shared/components/atoms/tooltips/tooltip';
import ActionsSidebar from 'src/modules/shared/components/molecules/actions-sidebar';
import type { SidebarAction } from 'src/modules/shared/components/molecules/actions-sidebar';
import {
  isTestAddress,
  isTestConversationDocument,
} from 'src/modules/communications/utils/test-conversation.util';
import { TestConversationBadge } from 'src/modules/communications/components/molecules/test-conversations/test-conversation-badge';

interface PhoneState {
  isLoading: boolean;
  hasError: boolean;
  isReady: boolean;
  isDisabled: boolean;
}

interface BasicColumnsParams {
  users?: { id: number; firstName: string; lastName: string }[];
  statuses?: Pick<FunnelStatus, 'id' | 'name' | 'terminalOutcome'>[];
  testAddressReadOnlyTooltip?: string;
  selectedConversationId?: number;
  onConversationSelect?: (id: number) => void;
  handleStatusChange: (conversationId: number, statusId: number) => void;
  handleUserAssign: (conversationId: number, userId: number | null) => void;
  handleCall?: (phoneNumber: string) => void;
  handleArchive?: (conversationId: number) => Promise<void>;
  phoneState?: PhoneState;
  updatingConversation: boolean;
  variant?: keyof typeof tableVariantClasses;
  handleUpdateAddress?: (
    addressId: number,
    data: { address: string }
  ) => Promise<void>;
  updatingAddressId?: number;
  powerDialingActive?: boolean;
  currentPowerDialingId?: string | null;
  currentPhoneNumber?: string | null;
}

export const createBasicColumns = ({
  users = [],
  statuses = [],
  testAddressReadOnlyTooltip,
  selectedConversationId,
  onConversationSelect,
  handleStatusChange,
  handleUserAssign,
  handleCall,
  handleArchive,
  phoneState = {
    isLoading: false,
    hasError: false,
    isReady: true,
    isDisabled: false,
  },
  updatingConversation,
  variant = 'primary',
  handleUpdateAddress,
  updatingAddressId,
  powerDialingActive = false,
  currentPowerDialingId = null,
  currentPhoneNumber = null,
}: BasicColumnsParams): TableColumnProps<FunnelConversationDocument>[] => {
  const { text, hoverText } = tableVariantClasses[variant];

  return [
    {
      header: 'Contacto',
      accessor: 'id',
      render: (_value: unknown, row: FunnelConversationDocument) => {
        const contacts = row.contacts;
        if (!contacts || contacts.length === 0) return null;

        const contact = contacts[0];
        const isSelected = row.id === selectedConversationId?.toString();
        const isTestConversation = isTestConversationDocument(row);
        const buttonClassName = isSelected ? '' : `${hoverText}`;

        return (
          <div className="flex w-full items-center justify-between">
            <div className="flex min-w-0 items-center gap-2">
              <Typography
                className={`cursor-pointer truncate text-xs font-semibold ${
                  isSelected ? hoverText : text
                } ${hoverText}`}
                onClick={() => onConversationSelect?.(Number(row.id))}
              >
                {formatFullName(contact.firstName, contact.lastName)}
              </Typography>
              {isTestConversation && <TestConversationBadge />}
            </div>

            <Tooltip
              size="sm"
              content="Abre la conversación"
              className="whitespace-nowrap"
            >
              <Button
                variant={isSelected ? 'primary' : 'secondary'}
                appearance="ghost"
                size="sm"
                onClick={() => {
                  onConversationSelect?.(Number(row.id));
                }}
                isIconOnly
                className={buttonClassName}
              >
                <PiCaretRightBold className="h-5 w-5" />
              </Button>
            </Tooltip>
          </div>
        );
      },
    },
    {
      header: 'Teléfonos',
      accessor: 'phone_addresses',
      render: (_value: unknown, row: FunnelConversationDocument) => {
        const contacts = row.contacts;
        if (!contacts || contacts.length === 0 || !contacts[0].addresses) {
          return null;
        }

        const addresses = contacts[0].addresses;
        const phoneAddresses = addresses.filter(
          (a) =>
            a.channelType === ChannelType.PHONE ||
            a.channelType === ChannelType.WHATSAPP
        );

        if (phoneAddresses.length === 0) return null;

        const isCurrentlyDialing =
          powerDialingActive && row.id === currentPowerDialingId;

        return (
          <div className="flex flex-col gap-1">
            {phoneAddresses.map((address) => {
              const isValidPhone = isValidPhoneNumber(address.address);
              const isUpdating = updatingAddressId === address.id;
              const isCurrentPhone =
                isCurrentlyDialing && address.address === currentPhoneNumber;

              return (
                <div key={address.id} className="flex items-center gap-2">
                  {isCurrentPhone && (
                    <AnimatedDot size="xs" className="!bg-call-500" />
                  )}
                  <EditableAddressField
                    key={address.id}
                    address={address.address}
                    channelType={address.channelType}
                    onSave={async ({ address: newAddress }) => {
                      if (!handleUpdateAddress) return;

                      await handleUpdateAddress(address.id, {
                        address: newAddress,
                      });
                    }}
                    onAction={
                      isValidPhone && handleCall ? handleCall : undefined
                    }
                    loading={isUpdating}
                    placeholder="Ingrese teléfono..."
                    actionDisabled={isActionDisabled(
                      ChannelType.PHONE,
                      phoneState
                    )}
                    actionLoading={phoneState.isLoading}
                    actionTooltip={getActionTooltip(
                      ChannelType.PHONE,
                      phoneState,
                      isValidPhone
                    )}
                    readOnlyTooltip={
                      isTestAddress(address.address)
                        ? testAddressReadOnlyTooltip
                        : undefined
                    }
                    showUtilityMenu={false}
                  />
                </div>
              );
            })}
          </div>
        );
      },
    },
    {
      header: 'Correos',
      accessor: 'email_addresses',
      render: (_value: unknown, row: FunnelConversationDocument) => {
        const contacts = row.contacts;
        if (!contacts || contacts.length === 0 || !contacts[0].addresses) {
          return null;
        }

        const addresses = contacts[0].addresses;
        const emailAddresses = addresses.filter(
          (a) => a.channelType === ChannelType.EMAIL
        );

        if (emailAddresses.length === 0) return null;

        return (
          <div className="flex w-full flex-col gap-1">
            {emailAddresses.map((address) => {
              const isUpdating = updatingAddressId === address.id;

              return (
                <div key={address.id} className="w-full">
                  <EditableAddressField
                    address={address.address}
                    channelType={address.channelType}
                    onSave={async ({ address: newAddress }) => {
                      if (!handleUpdateAddress) return;

                      await handleUpdateAddress(address.id, {
                        address: newAddress,
                      });
                    }}
                    loading={isUpdating}
                    onAction={() => {
                      return;
                    }}
                    placeholder="Ingrese correo..."
                    readOnlyTooltip={
                      isTestAddress(address.address)
                        ? testAddressReadOnlyTooltip
                        : undefined
                    }
                    showUtilityMenu={false}
                  />
                </div>
              );
            })}
          </div>
        );
      },
    },
    {
      header: 'Estado',
      accessor: 'funnelStatusId',
      render: (value: unknown, row: FunnelConversationDocument) => {
        const statusId = value as string;
        const currentStatus = statuses.find((s) => String(s.id) === statusId);

        const statusOptions = statuses.map((status) => ({
          id: status.id,
          content: (
            <FunnelStatusName
              name={status.name}
              outcome={status.terminalOutcome}
              icon="archive"
              showArchiveTooltip
            />
          ),
          selected: String(status.id) === statusId,
        }));

        return (
          <TagDropdown
            content={
              <FunnelStatusName
                name={currentStatus?.name ?? 'Sin estado'}
                outcome={currentStatus?.terminalOutcome}
                icon="archive"
                showArchiveTooltip
              />
            }
            variant="primary"
            appearance="outline"
            size="sm"
            options={statusOptions}
            onOptionSelect={(option) =>
              handleStatusChange(Number(row.id), Number(option.id))
            }
            isLoading={updatingConversation}
          />
        );
      },
    },
    {
      header: 'Asignado',
      accessor: 'userId',
      render: (value: unknown, row: FunnelConversationDocument) => {
        const userId = value as number | null;
        const currentUser = users.find((user) => user.id === userId);

        const userOptions = [
          {
            id: -1,
            content: 'Sin asignar',
            selected: userId == null,
          },
          ...users.map((user) => ({
            id: user.id,
            content: formatFullName(user.firstName, user.lastName),
            selected: user.id === userId,
          })),
        ];

        return (
          <TagDropdown
            content={
              currentUser
                ? formatFullName(currentUser.firstName, currentUser.lastName)
                : 'Sin asignar'
            }
            variant="primary"
            appearance="outline"
            size="sm"
            options={userOptions}
            onOptionSelect={(option) =>
              handleUserAssign(
                Number(row.id),
                option.id === -1 ? null : Number(option.id)
              )
            }
            isLoading={updatingConversation}
          />
        );
      },
    },
    {
      header: 'Última interacción',
      accessor: 'lastInteraction',
      render: (value: unknown) => {
        const lastInteraction =
          value as FunnelConversationDocument['lastInteraction'];
        if (!lastInteraction) {
          return null;
        }

        const { modelType, direction, status, snippet } = lastInteraction;

        return (
          <div className="flex items-center gap-2">
            <div className="mt-1 flex items-center gap-1">
              {direction && (
                <DirectionIndicator
                  isOutbound={direction === FunnelInteractionDirection.OUTGOING}
                  className={
                    modelType === FunnelInteractionType.CALL
                      ? '!text-call-500'
                      : '!text-whatsapp-500'
                  }
                />
              )}
              {modelType === FunnelInteractionType.CALL ? (
                <CallStatusIndicator status={status} size="sm" />
              ) : (
                <StyledIcon
                  icon={<PiChatCircle className="h-4 w-4" />}
                  variant="secondary"
                  size="sm"
                  className="!text-whatsapp-500"
                />
              )}
            </div>
            <Typography
              className={`${tableCellText} ${text} max-w-[18rem] flex-1 truncate`}
            >
              {snippet}
            </Typography>
          </div>
        );
      },
    },
    {
      header: 'Fecha de actualización',
      accessor: 'updatedAt',
      render: (value: unknown) => (
        <Typography className={`${tableCellText} ${text}`}>
          {formatDate(value as string)}
        </Typography>
      ),
    },
    {
      header: 'Fecha de creación',
      accessor: 'createdAt',
      render: (value: unknown) => (
        <Typography className={`${tableCellText} ${text}`}>
          {formatDate(value as string)}
        </Typography>
      ),
    },
    {
      header: '',
      accessor: 'actions',
      className: 'w-16 !min-w-0 sticky right-0',
      render: (_value: unknown, row: FunnelConversationDocument) => {
        const isArchived = !!row.archivedAt;

        const actions: SidebarAction[] = handleArchive
          ? isArchived
            ? [
                {
                  icon: <PiArrowCounterClockwiseLight className="h-5 w-5" />,
                  label: 'Restaura la conversación',
                  onClick: (e: React.MouseEvent) => {
                    e.stopPropagation();
                    void handleArchive(Number(row.id));
                  },
                  variant: 'primary',
                },
              ]
            : [
                {
                  icon: <PiArchiveLight className="h-5 w-5" />,
                  label: 'Archiva la conversación',
                  onClick: (e: React.MouseEvent) => {
                    e.stopPropagation();
                    void handleArchive(Number(row.id));
                  },
                  variant: 'danger',
                },
              ]
          : [];

        return (
          <div className="flex h-full items-center justify-end pe-2">
            <ActionsSidebar
              actions={actions}
              placement="right"
              offsetX={-32}
              offsetY={-6}
            >
              <div className="h-8 w-8" />
            </ActionsSidebar>
          </div>
        );
      },
    },
  ];
};
